import { Geotab } from "@fairfleet/geotab";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useGeotabClient } from "@/components/GeotabClientContext";
import FeedFirmwareData from "@/pages/device-firmware/models/FeedFirmwareData";

interface MimeContent {
  mimeType: string;
  data: number;
}

async function fetchFeedTextMessages(
  geotabClient: Geotab,
  deviceIds: string[],
  fromVersions: Record<string, string>,
  signal?: AbortSignal,
) {
  const result: Record<string, FeedFirmwareData> = {};
  const newVersions: Record<string, string> = {};

  await Promise.all(
    deviceIds.map(async deviceId => {
      const res = await geotabClient.getFeed(
        "TextMessage",
        { deviceSearch: { id: deviceId } },
        fromVersions[deviceId] ?? null,
        100,
        signal,
      );

      newVersions[deviceId] = res.toVersion;

      res.data.forEach(e => {
        const m = e.messageContent;
        if (m.contentType === "MimeContent") {
          const mime: MimeContent = m as unknown as MimeContent;
          if (mime.mimeType === "response") {
            const answer = atob(mime.data.toString());
            const splitted = answer.split(",");
            if (splitted.length >= 2) {
              const serialNumber = splitted[1];
              const uReaderFirmware = splitted[2].split("-")[0];

              result[deviceId] = {
                uReaderFirmware,
                uReaderSerialNumber: serialNumber,
                uiid: atob(mime.data.toString()),
              };
            }
          }
        }
      });
    }),
  );

  return { data: result, newVersions };
}

export function useFeedTextMessage(deviceIds: string[]) {
  // eslint-disable-next-line unused-imports/no-unused-vars
  const [fromVersions, setFromVersions] = useState({});
  const { geotabClient, activeSessionId } = useGeotabClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["feedTextMessage", activeSessionId, deviceIds],
    queryFn: ({ signal }) =>
      fetchFeedTextMessages(geotabClient, deviceIds, fromVersions || {}, signal),
    placeholderData: keepPreviousData,
    refetchInterval: 5000,
  });

  return { data, isLoading, error };
}
